import jsPDF from "jspdf";
import moment from "moment";
import {
  DynamicChecklistExportDocument,
  ExportFieldRow,
} from "./dynamicChecklistExport";

function sanitizePdfText(input: any): string {
  if (input === null || input === undefined) return "";
  return String(input)
    .replace(/\u2022/g, "|")
    .replace(/\u2192/g, "->")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^\x20-\x7E\n\r\t]/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function formatValueWithVerify(row: ExportFieldRow): string {
  let val = sanitizePdfText(row.displayValue);
  if (row.verified === true) val += " [Verified]";
  else if (row.verified === false) val += " [Not verified]";
  return val;
}

/**
 * Generates a checklist PDF from a pre-built dynamic export document.
 */
export function generateDynamicChecklistPdf(
  listVal: any,
  docModel: DynamicChecklistExportDocument
): void {
  const pdf = new jsPDF("p", "mm", "a4");
  let yPos = 20;

  pdf.setFontSize(18);
  pdf.setFont("helvetica", "bold");
  pdf.text(`${listVal.title} - Checklist Preview`, 105, yPos, { align: "center" });

  yPos += 12;
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    sanitizePdfText(
      `${listVal.file_id} | Login at ${moment(listVal.login_dt).format("DD MMMM YYYY, hh:mm A")} | ${listVal.bank_name}`
    ),
    105,
    yPos,
    { align: "center" }
  );

  yPos += 10;
  pdf.text(
    sanitizePdfText(
      `Process Checklist Completed at ${moment(listVal.date).format("DD MMMM YYYY, hh:mm A")} | ${listVal.officerType} | Updated by ${listVal.downloadedBy}`
    ),
    105,
    yPos,
    { align: "center" }
  );

  yPos += 10;

  const checkPageBreak = (needed = 0) => {
    const pageHeight = pdf.internal.pageSize.height;
    const bottomMargin = 22;
    if (yPos + needed > pageHeight - bottomMargin) {
      pdf.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };

  const addSectionTitle = (title: string, bgColor?: string) => {
    const leftMargin = 10;
    const boxWidth = 190;
    const boxHeight = 10;
    const topPadding = 0;
    const bottomPadding = 8;

    checkPageBreak(topPadding + boxHeight + bottomPadding);
    yPos += topPadding;

    const fillColor: [number, number, number] =
      bgColor === "warning" ? [255, 243, 205] : [0, 123, 255];
    const textColor: [number, number, number] =
      bgColor === "warning" ? [133, 100, 4] : [255, 255, 255];

    pdf.setFillColor(...fillColor);
    pdf.rect(leftMargin, yPos, boxWidth, boxHeight, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...textColor);
    pdf.text(title, leftMargin + 5, yPos + boxHeight / 2 + 3);

    yPos += boxHeight + bottomPadding;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
  };

  const addSubSection = (title: string) => {
    const left = 12;
    const width = 186;
    const height = 8;
    checkPageBreak(height + 6);

    const safeTitle = sanitizePdfText(title);
    pdf.setFillColor(245, 246, 248);
    pdf.setDrawColor(230, 232, 236);
    pdf.rect(left, yPos, width, height, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(40, 40, 40);
    pdf.text(safeTitle, left + 4, yPos + 5.5);

    pdf.setTextColor(0, 0, 0);
    yPos += height + 5;
  };

  const addField = (label: string, value: string, indent = 0) => {
    // Two-column, single-line Q/A layout with wrapping.
    // This avoids the glyph-spacing issues we saw with special characters and
    // prevents label/value overlap on long text.
    const xBase = 15 + indent * 4;
    const contentWidth = 190 - (xBase - 10); // 10..200 = 190mm usable
    const colGap = 4;
    const labelColWidth = Math.min(78, Math.max(58, contentWidth * 0.42));
    const valueColWidth = Math.max(70, contentWidth - labelColWidth - colGap);
    const xLabel = xBase;
    const xValue = xBase + labelColWidth + colGap;

    const safeLabel = sanitizePdfText(label);
    const safeValue = sanitizePdfText(value);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    const labelLines = pdf.splitTextToSize(`${safeLabel}:`, labelColWidth);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    const valueLines = pdf.splitTextToSize(safeValue, valueColWidth);

    const lineHeight = 5;
    const rowLines = Math.max(labelLines.length, valueLines.length, 1);
    const neededHeight = rowLines * lineHeight + 2;
    checkPageBreak(neededHeight + 2);

    let cy = yPos + lineHeight;
    for (let i = 0; i < rowLines; i++) {
      const l = labelLines[i] ?? "";
      const v = valueLines[i] ?? "";
      if (l) {
        pdf.setFont("helvetica", "bold");
        pdf.text(l, xLabel, cy);
      }
      if (v) {
        pdf.setFont("helvetica", "normal");
        pdf.text(v, xValue, cy);
      }
      cy += lineHeight;
    }

    yPos += neededHeight + 1;
  };

  docModel.sections.forEach((section) => {
    addSectionTitle(
      section.title,
      section.headerVariant === "warning" ? "warning" : undefined
    );
    section.blocks.forEach((block) => {
      const blockIndent = block.title ? 1 : 0;
      if (block.title) addSubSection(block.title);
      block.rows.forEach((row) => {
        addField(
          row.label,
          formatValueWithVerify(row),
          (row.indent ?? 0) + blockIndent
        );
      });
      yPos += block.title ? 3 : 2;
    });
  });

  if (docModel.remarks) {
    checkPageBreak(20);
    addSectionTitle("Remarks", "warning");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(sanitizePdfText(docModel.remarks), 180);
    lines.forEach((line: string) => {
      checkPageBreak(6);
      pdf.text(line, 15, yPos);
      yPos += 6;
    });
  }

  const fileName = `${listVal.file_id || "checklist"}_checklist.pdf`;
  pdf.save(fileName);
}
